import { useNavigate } from 'react-router';
import { useTrainerWeekly } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDate, formatDay } from '../../lib/format';
import { attentionList, calmList, weeklyReasons } from '../../lib/weekly';
import { Card, CardHeader, ClientRow, EmptyState, SkeletonList } from '../../ui';

/**
 * „Traži pažnju” na vrhu trenerskog dashboarda — sekcija 37.
 *
 * Smisao je da trener zna GDJE se javiti, ne da čita sve. Zato je popis poredan
 * po hitnosti (nepročitana poruka i predani check-in traže odgovor danas),
 * a uz svakog klijenta stoji razlog, ne samo ime.
 */
export function WeeklyAttention() {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const weekly = useTrainerWeekly();

  return (
    <QueryBoundary
      query={weekly}
      skeleton={<SkeletonList rows={3} />}
      isEmpty={(d) => d.items.length === 0}
      empty={{
        message: t('weekly.empty'),
        actionLabel: t('shell.clients'),
        onAction: () => void navigate('/trener/klijenti'),
      }}
    >
      {(data) => {
        const urgent = attentionList(data.items);
        const calm = calmList(data.items);

        return (
          <div className="flex flex-col gap-5">
            <Card>
              <CardHeader
                title={t('weekly.title')}
                {...(data.weekStart
                  ? {
                      eyebrow: t('weekly.week', {
                        date: formatDate(`${data.weekStart}T00:00:00.000Z`, locale),
                      }),
                    }
                  : {})}
              />
              {urgent.length === 0 ? (
                <EmptyState message={t('weekly.allGood')} />
              ) : (
                <ul className="flex flex-col gap-3" data-testid="weekly-attention">
                  {urgent.map((row) => (
                    <li key={row.clientId} data-client={row.clientId} data-urgency={row.urgency}>
                      <ClientRow
                        name={row.name}
                        to={`/trener/klijenti/${row.clientId}`}
                        subtitle={
                          row.lastWorkoutAt
                            ? `${t('weekly.lastWorkout')}: ${formatDay(row.lastWorkoutAt, locale)}`
                            : `${t('weekly.lastWorkout')}: ${t('weekly.never')}`
                        }
                        flags={weeklyReasons(row, t)}
                        consistencyLabel={`${row.completed}/${row.prescribed}`}
                        consistencyCaption={t('weekly.workoutsCaption')}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {calm.length > 0 && (
              <Card>
                <CardHeader title={t('weekly.others')} />
                <ul className="flex flex-col gap-3">
                  {calm.map((row) => (
                    <li key={row.clientId}>
                      <ClientRow
                        name={row.name}
                        to={`/trener/klijenti/${row.clientId}`}
                        subtitle={`${t('weekly.adherence')}: ${row.adherence}%`}
                        consistencyLabel={`${row.completed}/${row.prescribed}`}
                        consistencyCaption={t('weekly.workoutsCaption')}
                      />
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        );
      }}
    </QueryBoundary>
  );
}
